package com.wintermar.inspection.feature.presentation.inspection.answer.widget.incomplete

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.animateContentSize
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import com.wintermar.inspection.core.ui.component.TextBodySmall
import com.wintermar.inspection.core.ui.component.TextBodyTiny
import com.wintermar.inspection.core.ui.theme.AppDimension
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import com.wintermar.inspection.core.ui.theme.color.model.ThemeColor
import com.wintermar.inspection.feature.domain.model.answer.SummaryAnswerModel
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateQuestionModel
import com.wintermar.inspection.feature.presentation.inspection.widget.AnswerChooseView
import inspection.composeapp.generated.resources.Res
import inspection.composeapp.generated.resources.ic_arrow_down
import inspection.composeapp.generated.resources.ic_arrow_up
import inspection.composeapp.generated.resources.ic_warning_triangle
import inspection.composeapp.generated.resources.need_documentation
import inspection.composeapp.generated.resources.need_documentation_answer
import inspection.composeapp.generated.resources.unanswered_question
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Composable
fun Incomplete(
    unAnswerList: List<SummaryAnswerModel>,
    needDocumentList: List<SummaryAnswerModel>,
    withHeader: Boolean = true,
    dimension: AppDimension = LocalAppTheme.current.dimension,
) {
    LazyColumn(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = dimension.spacing.space4),
        verticalArrangement = Arrangement.spacedBy(dimension.spacing.space3)
    ) {
        item {
            if (withHeader) IncompleteHeader(unAnswerList.size + needDocumentList.size)
            else Spacer(modifier = Modifier.height(dimension.spacing.space2))
        }

        if (unAnswerList.isNotEmpty()) item {
            Content(
                title = stringResource(Res.string.unanswered_question),
                items = unAnswerList
            )
        }

        if (needDocumentList.isNotEmpty()) item {
            Content(
                title = stringResource(Res.string.need_documentation),
                items = needDocumentList
            )
        }

        item {
            Spacer(modifier = Modifier.height(dimension.spacing.space2))
        }
    }
}

@Composable
private fun Content(
    title: String,
    items: List<SummaryAnswerModel>,
    dimension: AppDimension = LocalAppTheme.current.dimension,
    color: ThemeColor = LocalAppTheme.current.color
) {
    var expand by remember { mutableStateOf(true) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(
                width = 1.dp,
                color = color.border.subtle,
                shape = RoundedCornerShape(dimension.radius.md)
            )
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(
                    color = color.surface.brand.primarySubtle.default,
                    shape = RoundedCornerShape(
                        topStart = dimension.radius.md,
                        topEnd = dimension.radius.md,
                        bottomStart = if (expand) dimension.radius.none else dimension.radius.md,
                        bottomEnd = if (expand) dimension.radius.none else dimension.radius.md
                    )
                )
                .clip(
                    shape = RoundedCornerShape(
                        topStart = dimension.radius.md,
                        topEnd = dimension.radius.md,
                        bottomStart = if (expand) dimension.radius.none else dimension.radius.md,
                        bottomEnd = if (expand) dimension.radius.none else dimension.radius.md
                    )
                )
                .clickable {
                    expand = !expand
                }
                .padding(dimension.spacing.space3),
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            TextBodySmall(text = "$title (${items.size})")
            Icon(
                painter = painterResource(if (expand) Res.drawable.ic_arrow_up else Res.drawable.ic_arrow_down),
                tint = color.icon.subtle,
                contentDescription = title,
                modifier = Modifier.size(dimension.size.icon.s).animateContentSize()
            )
        }

        AnimatedVisibility(
            visible = expand
        ) {
            Column {
                items.forEachIndexed { index, item ->
                    val question = item.question
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(dimension.spacing.space3),
                        verticalArrangement = Arrangement.spacedBy(
                            space = dimension.spacing.space2
                        )
                    ) {
                        TextBodyTiny(
                            text = item.pageTitle,
                            maxLines = 1,
                            color = { it.subtle }
                        )

                        TextBodyTiny(
                            text = item.sectionTitle,
                            maxLines = 1,
                            bold = true,
                            color = { it.subtle }
                        )

                        TextBodyTiny(
                            text = question.questionText,
                            bold = true,
                        )

                        if (question.answer.isEmptyDocumentWithAnswerOption(question.answerType)) {
                            AnswerContent(
                                question = question
                            )
                        }
                    }

                    if (index < items.size - 1) {
                        HorizontalDivider(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(vertical = dimension.spacing.space2),
                            color = color.border.subtle
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun AnswerContent(
    question: InspectionTemplateQuestionModel,
    dimension: AppDimension = LocalAppTheme.current.dimension,
) {
    Column(
        verticalArrangement = Arrangement.spacedBy(
            space = dimension.spacing.space1
        )
    ) {
        AnswerChooseView(
            answerType = question.answerType,
            answerOptionId = question.answer.answerOptionId,
            templateOptions = question.answerOptions
        )

        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(dimension.spacing.space2)
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_warning_triangle),
                tint = LocalAppTheme.current.color.icon.warning.default,
                modifier = Modifier.size(dimension.size.icon.s),
                contentDescription = question.questionText
            )
            TextBodySmall(
                text = stringResource(Res.string.need_documentation_answer),
                color = { it.warning.default })
        }
    }
}
